import { OccupancyLease } from './types'

export type PoolPetBucket =
    | 'requested'
    | 'has_pet'
    | 'no_roommate_pets'
    | 'other'

export type PoolBucketKey = PoolPetBucket | 'lifestyle_incomplete'

export const POOL_PET_BUCKET_ORDER: PoolPetBucket[] = [
    'requested',
    'has_pet',
    'no_roommate_pets',
    'other',
]

export const POOL_BUCKET_ORDER: PoolBucketKey[] = [
    ...POOL_PET_BUCKET_ORDER,
    'lifestyle_incomplete',
]

export const POOL_BUCKET_LABEL: Record<PoolBucketKey, string> = {
    requested: 'Requested roommate',
    has_pet: 'Has a pet',
    no_roommate_pets: 'Not okay with roommate pets',
    other: 'Everyone else',
    lifestyle_incomplete: 'Lifestyle incomplete',
}

export const POOL_PET_BUCKET_LABEL: Record<PoolPetBucket, string> = {
    requested: POOL_BUCKET_LABEL.requested,
    has_pet: POOL_BUCKET_LABEL.has_pet,
    no_roommate_pets: POOL_BUCKET_LABEL.no_roommate_pets,
    other: POOL_BUCKET_LABEL.other,
}

export function poolPetBucket(lease: OccupancyLease): PoolPetBucket {
    const facts = lease.occupancy_facts
    const hasRequest =
        (facts?.mutual_with || []).length > 0 ||
        (facts?.requested || []).some((row) => row.status !== 'mutual')
    if (hasRequest) return 'requested'
    if (facts?.has_pet) return 'has_pet'
    if (facts?.comfortable_with_roommate_pets === false)
        return 'no_roommate_pets'
    return 'other'
}

export function poolBucket(lease: OccupancyLease): PoolBucketKey {
    if (!lease.lifestyle_complete) return 'lifestyle_incomplete'
    return poolPetBucket(lease)
}

export interface PoolBucketGroup {
    key: PoolBucketKey
    label: string
    leases: OccupancyLease[]
}

export interface PoolFloorPlanGroup {
    key: string
    label: string
    count: number
    buckets: PoolBucketGroup[]
}

function floorPlanKey(lease: OccupancyLease) {
    return String(lease.unit_config ?? lease.unit_config_name ?? 'none')
}

function floorPlanLabel(lease: OccupancyLease) {
    return lease.unit_config_name || 'No floor plan'
}

function sortPeople(leases: OccupancyLease[]) {
    return [...leases].sort((a, b) =>
        a.tenant_name.localeCompare(b.tenant_name, undefined, {
            numeric: true,
        }),
    )
}

export function groupUnassignedPool(
    leases: OccupancyLease[],
): PoolFloorPlanGroup[] {
    const byPlan = new Map<string, OccupancyLease[]>()
    for (const lease of leases) {
        const key = floorPlanKey(lease)
        const existing = byPlan.get(key)
        if (existing) existing.push(lease)
        else byPlan.set(key, [lease])
    }
    const groups: PoolFloorPlanGroup[] = []
    for (const [key, people] of Array.from(byPlan.entries())) {
        const byBucket = new Map<PoolBucketKey, OccupancyLease[]>()
        for (const lease of people) {
            const bucket = poolBucket(lease)
            const list = byBucket.get(bucket)
            if (list) list.push(lease)
            else byBucket.set(bucket, [lease])
        }
        groups.push({
            key,
            label: floorPlanLabel(people[0]),
            count: people.length,
            buckets: POOL_BUCKET_ORDER.filter(
                (bucket) => (byBucket.get(bucket) || []).length > 0,
            ).map((bucket) => ({
                key: bucket,
                label: POOL_BUCKET_LABEL[bucket],
                leases: sortPeople(byBucket.get(bucket) || []),
            })),
        })
    }
    groups.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
    )
    return groups
}
